import React, {useEffect, useMemo, useState} from "react";
import {
  DollarOutlined,
  EditOutlined,
  LockOutlined,
  MailOutlined,
  PercentageOutlined,
  SettingOutlined,
  UserOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import {Button, Form, Input, message, Modal, Tag, Tooltip, Typography} from "antd";
import ProfileItem from "./ProfileItem";

const {Text} = Typography;

const v = (value) =>
  value === null || value === undefined || value === "" ? "NaN" : value;

const EDITABLE_FIELDS = ["full_name", "telegram_id"];

export default function ProfileCard({profile, onUpdate}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const initialValues = useMemo(() => {
    return {
      full_name: profile?.full_name ?? "",
      telegram_id: profile?.telegram_id ?? "",
      email: profile?.email ?? "",
    };
  }, [profile]);

  useEffect(() => {
    if (open) form.setFieldsValue(initialValues);
  }, [open, form, initialValues]);

  const close = () => setOpen(false);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const patch = {};
      for (const k of EDITABLE_FIELDS) patch[k] = values[k];

      setSaving(true);
      await Promise.resolve(onUpdate?.(patch));
      message.success("Профиль обновлён");
      setOpen(false);
    } catch (e) {
      // validateFields кидаем исключение
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="pb-8">
      <div className="bg-white rounded-[20px] shadow-lg p-4 lg:p-6">
        <div className="flex items-center justify-between pb-4 border-b">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-[var(--blue-3)]">
              <UserOutlined/>
            </div>

            <h2 className="m-0 font-semibold text-[16px] lg:text-[20px] text-[#1A1A1A]">
              Профиль пользователя
            </h2>
          </div>

          <div className="flex items-center gap-3">
            <Button
              type="default"
              icon={<EditOutlined/>}
              onClick={() => setOpen(true)}
              className="hidden lg:inline-flex h-[38px] px-5 items-center justify-center gap-2 border border-[#D9D9D9] rounded-[10px] text-[#1A1A1A] font-medium bg-white"
            >
              Редактировать
            </Button>
            <Tooltip title="Функция находится на этапе разработки, обратитесь к администратору при необходимости">

              <Button
                disabled
                type="default"
                icon={<LockOutlined/>}
                className="hidden lg:inline-flex h-[38px] px-5 items-center justify-center gap-2 border border-[#D9D9D9] rounded-[10px] text-[#1A1A1A] font-medium bg-white"
              >
                Изменить пароль
              </Button>
            </Tooltip>
            <EditOutlined
              className="lg:hidden text-[#8C8C8C]"
              onClick={() => setOpen(true)}
            />
          </div>
        </div>

        <div className="mt-4 text-[#1A1A1A] flex flex-col gap-4 lg:grid lg:grid-cols-4 lg:gap-x-10 lg:gap-y-8">
          <ProfileItem
            icon={<UserOutlined/>}
            label="ФИО пользователя"
            value={v(profile?.full_name)}
          />

          <ProfileItem
            icon={<MailOutlined/>}
            label="Основной email"
            value={v(profile?.email)}
          />

          <ProfileItem
            icon={<MailOutlined/>}
            label="Технический email"
            value={v(profile?.technical_email ?? profile?.tech_email)}
          />

          <ProfileItem
            icon={<WalletOutlined/>}
            label="Текущий баланс"
            value={
              profile?.balance != null ? (
                <Text className="text-[var(--blue-3)]">{profile.balance}</Text>
              ) : (
                "NaN"
              )
            }
          />

          <ProfileItem
            icon={<SettingOutlined/>}
            label="Тип клиента"
            value={
              profile?.client_type ? (
                <Tag
                  className="inline-flex items-center px-3 py-1 text-[13px] rounded-full bg-blue-100 text-[var(--blue-3)] border-0">
                  {profile.client_type}
                </Tag>
              ) : (
                "NaN"
              )
            }
          />

          <ProfileItem
            icon={<SettingOutlined/>}
            label="Тип комиссии"
            value={v(profile?.commission_type)}
          />

          <ProfileItem
            icon={<PercentageOutlined/>}
            label="Процент комиссии"
            value={
              profile?.commission_percent != null
                ? `${profile.commission_percent} %`
                : "NaN"
            }
          />

          <ProfileItem
            icon={<DollarOutlined/>}
            label="Основная валюта"
            value={v(profile?.base_currency)}
          />
        </div>

        <Button
          type="default"
          icon={<LockOutlined/>}
          className="mt-6 w-full h-[44px] flex items-center justify-center gap-2 border border-[#D9D9D9] rounded-[10px] text-[#1A1A1A] font-medium lg:hidden bg-white"
          disabled
        >
          Изменить пароль
        </Button>
      </div>

      <Modal
        open={open}
        onCancel={close}
        onOk={handleSave}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={saving}
        title="Редактирование профиля"
        destroyOnHidden
      >
        <Form form={form} layout="vertical" initialValues={initialValues}>
          <Form.Item
            label="ФИО пользователя"
            name="full_name"
            rules={[{required: true, message: "Введите ФИО"}]}
          >
            <Input placeholder="Иван Иванов"/>
          </Form.Item>

          <Form.Item
            label="Telegram ID(s)"
            name="telegram_id"
            tooltip="Можно несколько но обязательно через запятую."
          >
            <Input placeholder="123456789, 987654321"/>
          </Form.Item>


        </Form>
      </Modal>
    </section>
  );
}